import { useCallback, useEffect, useRef, useState } from 'react';

type Params<T> = {
  /** Name of the broadcast channel. Add more channel names as needed */
  name: 'tiktok-auth' | 'slack-auth' | 'youtube-auth';
  /** For initializing `message` with a default value before any messages are received */
  defaultValue?: T;
};

// Overloading for when a default value is provided
export function useBroadcastChannel<T>(
  params: Params<T> & { defaultValue: T }
): {
  message: T;
  postMessage: (message: T) => void;
  closeChannel: () => void;
};

export function useBroadcastChannel<T>(params: Params<T>): {
  message: T | undefined;
  postMessage: (message: T) => void;
  closeChannel: () => void;
};

export function useBroadcastChannel<T>({ name, defaultValue }: Params<T>) {
  const [message, setMessage] = useState<T | undefined>(defaultValue);
  const bcRef = useRef(new BroadcastChannel(name));

  useEffect(() => {
    const bc = bcRef.current;
    bc.onmessage = (event) => {
      setMessage(event.data);
    };
  }, []);

  const postMessage = useCallback((message: T) => {
    try {
      bcRef.current.postMessage(message);
    } catch (error) {
      console.error('Error posting message to broadcast channel', error);
    }
  }, []);

  const closeChannel = useCallback(() => {
    try {
      bcRef.current.close();
    } catch (error) {
      console.error('Error closing broadcast channel', error);
    }
  }, []);

  return {
    /** The current message of the broadcast channel */
    message,
    /** Send a message to the broadcast channel */
    postMessage,
    /** Close the broadcast channel */
    closeChannel,
  };
}
